import { useMutation, useQuery } from "@apollo/client";
import {
  Avatar,
  Card,
  CrossIcon,
  Dialog,
  EditIcon,
  Heading,
  IconButton,
  Label,
  Menu,
  MoreIcon,
  Pane,
  Paragraph,
  Popover,
  Position,
  Select,
  SideSheet,
  Spinner,
  Tab,
  Table,
  Tablist,
  TextInputField,
  TickIcon,
  toaster,
  TrashIcon,
} from "evergreen-ui";
import React, { useState } from "react";
import { UPDATE_PACKAGE_ORDER } from "src/AdminRoutes/AdminRestockOrders/AdminRestockMutation";
import useInput from "src/Hooks/useInput";
import { DELETE_PACKAGE_ORDER_BY_PK } from "src/Mutations/Orders.share";
import { GET_USER_BY_ID } from "src/Queries/users.shared";
import { getDateWithTime } from "src/utilities";
import Button from "../Button";
import Status from "../Status";

const AdminRestockOrderRow = ({
  id,
  username,
  avatar,
  quantity,
  amount,
  actualQuantity,
  actualSystemOffer,
  packageId,
  bonus_period,
  payment,
  upline,
  create_date,
  buyerId,
  sellerId,
  refetch,
  sellingPrice,
  packages,
  status,
  packageName,
  showColumn,
}) => {
  const [showSideSheet, setShowSideSheet] = useState<boolean>(false);
  const [showDelete, setShowDelete] = useState<boolean>(false);

  const [isLoading, setIsLoading] = useState(false);
  const quantityInput = useInput(actualQuantity);
  const confirmDelete = useInput("");
  const systemOfferInput = useInput(actualSystemOffer);
  const priceInput = useInput(sellingPrice);
  const [selectedTab, setSelectedTab] = useState("order");
  const [selectedPackage, setSelectedPackage] = useState({
    id: packageId,
    name: packageName,
  });

  const { data, loading, error } = useQuery(GET_USER_BY_ID, {
    variables: {
      id: selectedTab === "upline" ? sellerId : buyerId,
    },
    fetchPolicy: "network-only",
  });
  const [updateOrder] = useMutation(UPDATE_PACKAGE_ORDER);
  const [deleteOrder] = useMutation(DELETE_PACKAGE_ORDER_BY_PK);
  const toggleSideSheet = () => {
    setShowSideSheet(!showSideSheet);
  };
  const toggleDelete = () => {
    setShowDelete(!showDelete);
    deleteOrder({
      variables: {
        id,
      },
    });
  };

  const onConfirmDelete = () => {
    setIsLoading(true);
    toaster.warning("Deleting order...");
    deleteOrder({
      variables: {
        id,
      },
    })
      .then(() => {
        toaster.closeAll();
        toaster.success("Order deleted!");
        refetch().then(() => {
          setIsLoading(false);
          setShowDelete(false);
        });
      })
      .catch((e) => {
        toaster.closeAll();
        toaster.danger(e.message);
      });
  };
  const onSaveClicked = (e) => {
    e.preventDefault();
    setIsLoading(true);
    toaster.warning("Saving changes...");
    updateOrder({
      variables: {
        id,
        set: {
          selling_price: priceInput.value,
          quantity: quantityInput.value,
          system_offer: systemOfferInput.value,
          package_id: selectedPackage.id,
        },
      },
    })
      .then(() => {
        toaster.closeAll();
        toaster.success("Changes saved!");
        refetch().then(() => {
          setIsLoading(false);
        });
      })
      .catch((e) => {
        toaster.closeAll();
        toaster.danger(e.message);
        setIsLoading(false);
      });
  };

  const onUpdateStatus = (newStatus) => {
    toaster.warning("Saving changes...");
    updateOrder({
      variables: {
        id,
        set: { status: newStatus },
      },
    })
      .then(() => {
        toaster.closeAll();
        toaster.success("Changes saved!");
        refetch();
      })
      .catch((e) => {
        toaster.closeAll();
        toaster.danger(e.message);
        refetch();
      });
  };

  return (
    <Table.Row>
      <Table.TextCell>
        <span
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          {" "}
          <Avatar marginRight=".2rem" name={username} src={avatar} size={24} />
          {username}
        </span>
      </Table.TextCell>
      <Table.TextCell>{quantity}</Table.TextCell>
      <Table.TextCell>{packageName}</Table.TextCell>
      <Table.TextCell>{showColumn}</Table.TextCell>
      <Table.TextCell>
        <Status status={status} />
      </Table.TextCell>
      <Table.Cell>
        <Popover
          position={Position.BOTTOM_RIGHT}
          content={({ close }) => (
            <Menu>
              <Menu.Group title="Basic Operations">
                <Menu.Item
                  disabled={status === "Approved"}
                  onSelect={() => {
                    onUpdateStatus("Approved");
                    close();
                  }}
                  icon={TickIcon}
                  intent="success"
                >
                  Approve
                </Menu.Item>
                <Menu.Item
                  disabled={status === "Rejected"}
                  onSelect={() => {
                    onUpdateStatus("Rejected");
                    close();
                  }}
                  icon={CrossIcon}
                  intent="warning"
                >
                  Reject
                </Menu.Item>
              </Menu.Group>
              <Menu.Divider />
              <Menu.Group title="Advanced Operations">
                <Menu.Item onSelect={toggleSideSheet} icon={EditIcon}>
                  Modify
                </Menu.Item>
                <Menu.Item
                  onSelect={toggleDelete}
                  icon={TrashIcon}
                  intent="danger"
                >
                  Delete
                </Menu.Item>
              </Menu.Group>
            </Menu>
          )}
        >
          <IconButton appearance="minimal" height={24} icon={MoreIcon} />
        </Popover>
      </Table.Cell>
      <Dialog
        intent="danger"
        confirmLabel="Confirm Delete"
        onConfirm={onConfirmDelete}
        onCloseComplete={() => {
          setIsLoading(false);
          setShowDelete(false);
        }}
        isConfirmLoading={isLoading}
        isConfirmDisabled={parseInt(confirmDelete.value) !== parseInt(id)}
        isShown={showDelete}
        title="Confirm delete order?"
      >
        <Heading>**WARNING**</Heading>
        <Paragraph>THIS ACTION CANNOT BE REVERSED!</Paragraph>
        <TextInputField
          label={`Repeat the order's ID to continue: ${id}`}
          {...confirmDelete}
        />
      </Dialog>
      <SideSheet
        preventBodyScrolling
        containerProps={{
          display: "flex",
          flex: "1",
          flexDirection: "column",
          maxWidth: "100vw",
        }}
        isShown={showSideSheet}
      >
        <Pane zIndex={1} flexShrink={0} elevation={0} backgroundColor="white">
          <Pane padding={16}>
            <Pane
              flexDirection="row"
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Pane display="flex" flexDirection="column">
                <Label>{getDateWithTime(create_date)}</Label>
                <Heading>
                  {username}'s Order (ID: {id})
                </Heading>
                <Paragraph>
                  {packageName} | Quantity: {quantity}
                </Paragraph>
                <Pane>
                  <Status status={status} />
                  {payment}
                </Pane>
              </Pane>
              <IconButton icon={CrossIcon} onClick={toggleSideSheet} />
            </Pane>
          </Pane>
          <Pane>
            <Tablist>
              <Tab
                isSelected={selectedTab === "order"}
                onSelect={() => {
                  setSelectedTab("order");
                }}
              >
                Order
              </Tab>
              <Tab
                isSelected={selectedTab === "agent"}
                onSelect={() => {
                  setSelectedTab("agent");
                }}
              >
                Agent
              </Tab>
              <Tab
                isSelected={selectedTab === "upline"}
                onSelect={() => {
                  setSelectedTab("upline");
                }}
              >
                Upline
              </Tab>
            </Tablist>
          </Pane>
        </Pane>
        <Pane
          paddingBottom={"20vh"}
          flex={1}
          overflowY="scroll"
          background="tint1"
          padding={16}
        >
          {selectedTab === "order" && (
            <Card
              marginBottom="20vh"
              backgroundColor="white"
              elevation={0}
              padding="1rem"
              flexDirection="column"
              display="flex"
            >
              <Heading>Order Details</Heading>
              <TextInputField
                type="number"
                label="Quantity"
                {...quantityInput}
                hint="Quantity will affect agent's upline's stock balance unless the order is paid to your company. It is used on most packages that has no offer."
              />
              <TextInputField
                type="number"
                label="Company Offer"
                {...systemOfferInput}
                hint="Company Offer will give agents stocks without directly charging them. It is usually used on offers like 3+1 or 招募金券 where uplines collect money and company provides stock. (e.g. In the case of 3+1, the company offer will be 1 and Quantity will be 3."
              />
              <Label>Package</Label>
              <Select
                value={selectedPackage.name}
                onChange={(e) => {
                  let thisPackage = packages[e.target.value];
                  setSelectedPackage({
                    id: thisPackage.id,
                    name:
                      thisPackage.chinese_name +
                      " | " +
                      thisPackage.name +
                      "(" +
                      thisPackage.package_country.name +
                      ")",
                  });
                }}
              >
                <option>[SELECTED] {selectedPackage.name}</option>
                {packages.map((pac, index) => (
                  <option value={index} key={index}>
                    {pac.chinese_name} | {pac.name} ({pac.package_country.name})
                  </option>
                ))}
              </Select>
              <TextInputField
                marginTop="1rem"
                type="number"
                {...priceInput}
                label="Price"
                hint="Currency will be same as the currency of country you chose for the package."
              />
              <Button
                onClick={onSaveClicked}
                loading={isLoading}
                label="Save"
                disabled={
                  (actualQuantity === parseInt(quantityInput.value) &&
                    actualSystemOffer === parseInt(systemOfferInput.value) &&
                    parseInt(packageId) === parseInt(selectedPackage.id) &&
                    sellingPrice === parseInt(priceInput.value)) ||
                  quantityInput.value === "" ||
                  systemOfferInput.value === "" ||
                  priceInput.value === ""
                }
              />
            </Card>
          )}
          {!loading && error && error.message}
          {loading && <Spinner />}
          {(selectedTab === "agent" || selectedTab === "upline") &&
            !loading &&
            data && (
              <Card
                backgroundColor="white"
                elevation={0}
                flexDirection="column"
                display="flex"
              >
                <Heading padding="1rem">
                  {selectedTab === "agent" ? "Agent" : "Upline"} Details
                </Heading>
                {data.users_by_pk ? (
                  <Pane background="tint1" flex={1} padding="1rem">
                    <Label>ID: {data.users_by_pk.id}</Label>
                    <Paragraph>Name: {data.users_by_pk.name}</Paragraph>
                    <Label>Username: {data.users_by_pk.username}</Label>
                    <Paragraph>
                      Join date: {getDateWithTime(data.users_by_pk.created_at)}
                    </Paragraph>
                    <Label>Contact No: {data.users_by_pk.phone}</Label>
                    <Paragraph>E-mail: {data.users_by_pk.email}</Paragraph>
                    <Label>
                      Stock Balance: {data.users_by_pk.stock_balance}
                    </Label>
                  </Pane>
                ) : (
                  <Pane background="tint1" flex={1} padding="1rem">
                    <Paragraph>
                      {selectedTab === "agent"
                        ? "User not found!"
                        : "User has no upline!"}
                    </Paragraph>
                  </Pane>
                )}
              </Card>
            )}
        </Pane>
      </SideSheet>
    </Table.Row>
  );
};

export default AdminRestockOrderRow;
